import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const MARGIN = { top: 20, right: 30, bottom: 40, left: 50 };
const ROW_HEIGHT = 30;

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '4px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '12px',
  },
  container: {
    overflowX: 'auto',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
};

function getDotColor(avgPrice, nationalAvg) {
  const pctDiff = Math.abs(avgPrice - nationalAvg) / nationalAvg;
  if (pctDiff <= 0.01) return '#f59e0b'; // amber — within 1%
  if (avgPrice > nationalAvg) return '#dc2626'; // red — above avg
  return '#16a34a'; // green — below avg
}

export default function LollipopChart({ data }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    if (!data || !data.states || data.states.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const { states, nationalAvg } = data;

    // Sort by avgPrice descending (most expensive at top)
    const sorted = [...states].sort((a, b) => b.avgPrice - a.avgPrice);

    const containerWidth = containerRef.current
      ? containerRef.current.clientWidth
      : 700;
    const width = Math.max(500, containerWidth);
    const height = sorted.length * ROW_HEIGHT + MARGIN.top + MARGIN.bottom;
    const innerW = width - MARGIN.left - MARGIN.right;
    const innerH = height - MARGIN.top - MARGIN.bottom;

    svg.attr('width', width).attr('height', height);

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    // Scales
    const xMax = d3.max(sorted, (d) => d.avgPrice);
    const xPadding = xMax * 0.15 || 5;

    const x = d3.scaleLinear()
      .domain([0, xMax + xPadding])
      .nice()
      .range([0, innerW]);

    const y = d3.scaleBand()
      .domain(sorted.map((d) => d.state))
      .range([0, innerH])
      .padding(0.3);

    // Vertical grid lines
    g.selectAll('.grid-v')
      .data(x.ticks())
      .enter()
      .append('line')
      .attr('class', 'grid-v')
      .attr('x1', (d) => x(d))
      .attr('x2', (d) => x(d))
      .attr('y1', 0)
      .attr('y2', innerH)
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // X axis (bottom)
    g.append('g')
      .attr('transform', `translate(0, ${innerH})`)
      .call(
        d3.axisBottom(x)
          .ticks(6)
          .tickSize(0)
          .tickFormat((d) => `$${d}`)
      )
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    // Y axis (left — state abbreviations)
    g.append('g')
      .call(d3.axisLeft(y).tickSize(0))
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '11px')
      .attr('fill', '#374151')
      .attr('font-weight', '500');

    // National average reference line
    const avgX = x(nationalAvg);

    g.append('line')
      .attr('x1', avgX)
      .attr('x2', avgX)
      .attr('y1', 0)
      .attr('y2', innerH)
      .attr('stroke', '#2563eb')
      .attr('stroke-width', 1.5)
      .attr('stroke-dasharray', '6,3');

    g.append('text')
      .attr('x', avgX)
      .attr('y', -6)
      .attr('text-anchor', 'middle')
      .attr('font-size', '10px')
      .attr('fill', '#2563eb')
      .attr('font-weight', '600')
      .text(`National Avg: $${nationalAvg.toFixed(2)}`);

    // Draw lollipops
    sorted.forEach((d) => {
      const cy = y(d.state) + y.bandwidth() / 2;
      const dotColor = getDotColor(d.avgPrice, nationalAvg);

      const rowGroup = g.append('g')
        .attr('class', 'lollipop-row')
        .style('cursor', 'pointer');

      // Invisible hit area
      rowGroup.append('rect')
        .attr('x', 0)
        .attr('y', y(d.state))
        .attr('width', innerW)
        .attr('height', y.bandwidth())
        .attr('fill', 'transparent');

      // Stem
      rowGroup.append('line')
        .attr('x1', x(0))
        .attr('x2', x(d.avgPrice))
        .attr('y1', cy)
        .attr('y2', cy)
        .attr('stroke', '#d1d5db')
        .attr('stroke-width', 2);

      // Dot
      rowGroup.append('circle')
        .attr('cx', x(d.avgPrice))
        .attr('cy', cy)
        .attr('r', 7)
        .attr('fill', dotColor)
        .attr('stroke', '#fff')
        .attr('stroke-width', 2);

      // Value label
      rowGroup.append('text')
        .attr('x', x(d.avgPrice) + 14)
        .attr('y', cy)
        .attr('dy', '0.35em')
        .attr('font-size', '10px')
        .attr('fill', dotColor)
        .text(`$${d.avgPrice.toFixed(2)} (${d.addressCount} addr)`);

      // Hover interactions
      const diff = d.avgPrice - nationalAvg;
      const diffSign = diff >= 0 ? '+' : '';

      rowGroup
        .on('mouseenter', (event) => {
          rowGroup.select('line').attr('stroke', '#9ca3af').attr('stroke-width', 3);
          rowGroup.select('circle').attr('r', 10);

          setTooltip({
            x: event.clientX + 12,
            y: event.clientY - 10,
            state: d.state,
            avgPrice: d.avgPrice,
            rank: d.rank,
            addressCount: d.addressCount,
            diff,
            diffSign,
          });
        })
        .on('mousemove', (event) => {
          setTooltip((prev) =>
            prev
              ? {
                  ...prev,
                  x: event.clientX + 12,
                  y: event.clientY - 10,
                }
              : prev
          );
        })
        .on('mouseleave', () => {
          rowGroup.select('line').attr('stroke', '#d1d5db').attr('stroke-width', 2);
          rowGroup.select('circle').attr('r', 7);
          setTooltip(null);
        });
    });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data]);

  if (!data || !data.states || data.states.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>State Price Ranking</div>
        <div style={styles.subtitle}>Average pest365 price per state, sorted by price</div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No ranking data available
        </div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.title}>State Price Ranking</div>
      <div style={styles.subtitle}>Average pest365 price per state, sorted by price</div>
      <div style={styles.container} ref={containerRef}>
        <svg ref={svgRef} />
      </div>
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {tooltip.state}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Avg Price</span>
            <span style={{ fontWeight: 600 }}>${tooltip.avgPrice.toFixed(2)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Rank</span>
            <span style={{ fontWeight: 600 }}>#{tooltip.rank}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Addresses</span>
            <span style={{ fontWeight: 600 }}>{tooltip.addressCount}</span>
          </div>
          <div style={{ borderTop: '1px solid #f3f4f6', marginTop: '4px', paddingTop: '4px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
              <span style={{ color: '#6b7280' }}>vs National Avg</span>
              <span style={{ fontWeight: 600, color: tooltip.diff >= 0 ? '#dc2626' : '#16a34a' }}>
                {tooltip.diffSign}${tooltip.diff.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
